// src/pages/auth/TwitterCallback.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Loader from '../../components/common/Loader';
import api from '../../services/api';
import SubscriptionService from '../../services/subscription.service';

interface DebugInfo {
  apiTest?: string | { status: number };
  stage?: string;
  status?: number;
  errorDetails?: any;
  directFetch?: any;
  testError?: any;
  fetchError?: any;
  planType?: string;
  planDuration?: string;
  paymentError?: any;
}

const TwitterCallback: React.FC = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();

  useEffect(() => {
    const handleTwitterCallback = async () => {
      try {
        // Get OAuth tokens from URL
        const params = new URLSearchParams(location.search);
        const oauthToken = params.get('oauth_token');
        const oauthVerifier = params.get('oauth_verifier');
        
        // Get plan info from sessionStorage
        const planType = sessionStorage.getItem('planType') || 'basic';
        const planDuration = sessionStorage.getItem('planDuration') || 'monthly';
        
        // Store plan info in debug
        setDebugInfo(prev => ({ ...prev, planType, planDuration }));
        
        // Clear session storage
        sessionStorage.removeItem('planType');
        sessionStorage.removeItem('planDuration');
        
        if (!oauthToken || !oauthVerifier) {
          throw new Error('No authorization parameters found');
        }

        // Debug info
        console.log('Twitter callback received:', { 
          oauthToken: oauthToken.substring(0, 6) + '...',
          oauthVerifier: oauthVerifier.substring(0, 6) + '...',
          apiBaseUrl: process.env.REACT_APP_API_URL,
          planType,
          planDuration
        });
        
        // First try a simple test request to verify API connectivity
        try {
          const testResponse = await api.get('/health');
          console.log('API connectivity test successful:', testResponse.status);
        } catch (testError) {
          console.warn('API connectivity test failed:', testError);
          setDebugInfo(prev => ({ ...prev, apiTest: 'failed', testError }));
        }

        // Attempt direct fetch as fallback for diagnostics only
        try {
          console.log('Attempting direct fetch as diagnostic...');
          const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/twitter/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ oauth_token: oauthToken, oauth_verifier: oauthVerifier })
          });
          
          console.log('Direct fetch result:', {
            status: directResponse.status,
            ok: directResponse.ok,
            statusText: directResponse.statusText
          });
          
          setDebugInfo(prev => ({ 
            ...prev, 
            directFetch: { 
              status: directResponse.status,
              ok: directResponse.ok 
            }
          }));
        } catch (fetchErr) {
          console.error('Direct fetch failed:', fetchErr);
          setDebugInfo(prev => ({ ...prev, directFetch: 'failed', fetchError: fetchErr }));
        }
        
        // Exchange tokens for JWT via API with detailed error tracking
        console.log('Attempting Twitter token exchange...');
        setDebugInfo(prev => ({ ...prev, stage: 'sending-request' }));
        
        const response = await api.post('/auth/twitter/exchange', {
          oauth_token: oauthToken,
          oauth_verifier: oauthVerifier
        });
        
        console.log('Twitter exchange successful:', response.status);
        setDebugInfo(prev => ({ ...prev, stage: 'received-response', status: response.status }));
        
        // Save the token and user data
        localStorage.setItem('token', response.data.accessToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Update auth context
        setAuthState({
          isAuthenticated: true,
          currentUser: response.data.user,
          loading: false,
          error: null
        });
        
        // Check if premium plan was selected
        if (planType !== 'basic') {
          setStatus('payment');
          setDebugInfo(prev => ({ ...prev, stage: 'payment-init', planType, planDuration }));
          
          try {
            // Initiate payment flow
            const paymentResponse = await SubscriptionService.upgrade(
              planType as 'premium' | 'ultimate',
              planDuration as 'monthly' | 'yearly'
            );
            
            if (paymentResponse.data && paymentResponse.data.authorizationUrl) {
              // Redirect to payment page
              window.location.href = paymentResponse.data.authorizationUrl;
              return; // Stop execution
            }
          } catch (paymentError) {
            console.error('Payment initiation failed:', paymentError);
            setDebugInfo(prev => ({ ...prev, paymentError }));
            // Continue to dashboard even if payment fails
          }
        }
        
        setStatus('success');
        
        // Redirect to dashboard
        setTimeout(() => {
          navigate('/admin');
        }, 1000);
      } catch (err: any) {
        console.error('Error during Twitter authentication:', err);
        
        // Detailed error diagnostics
        const errorDetails = {
          message: err?.message || 'Unknown error',
          isAxiosError: err?.isAxiosError || false,
          status: err?.response?.status,
          statusText: err?.response?.statusText,
          data: err?.response?.data,
          url: err?.config?.url,
          method: err?.config?.method,
          headers: err?.config?.headers,
          baseURL: err?.config?.baseURL,
          timeout: err?.code === 'ECONNABORTED' ? 'Request timed out' : 'N/A',
        };
        
        console.log('Detailed error diagnostics:', errorDetails);
        setDebugInfo(prev => ({ ...prev, stage: 'error', errorDetails }));
        
        setStatus('error');
        setError(
          err?.response?.data?.message || 
          err?.message || 
          'Failed to authenticate with Twitter. Please try again or use a different login method.'
        );
      }
    };

    handleTwitterCallback();
  }, [location, navigate, setAuthState]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded shadow-md max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <div className="text-xs text-gray-500 mb-4 overflow-hidden">
            <details>
              <summary>Debug Information</summary>
              <pre className="mt-2 p-2 bg-gray-100 rounded overflow-x-auto">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </details>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded shadow-md text-center">
        <Loader size="lg" />
        <h2 className="text-xl font-semibold mt-4">
          {status === 'success' ? 'Authentication Successful!' : 
           status === 'payment' ? 'Preparing Your Subscription...' : 
           'Authenticating with Twitter...'}
        </h2>
        <p className="text-gray-600 mt-2">
          {status === 'success' ? 'Redirecting you to your dashboard...' : 
           status === 'payment' ? 'Setting up your premium account...' :
           'Please wait while we complete your login.'}
        </p>
      </div>
    </div>
  );
};

export default TwitterCallback;